import React from 'react';
import { useDraggableInputFile } from './Hooks/useDraggableInputFile.inner.js';
import PropTypes from 'prop-types';
import Span from '../Texts/Span/Span';
import * as S from './DragAndDrop.styles';
import Icon from '../Icon/Icon';
import {
  MAX_CHAR_FILENAME_MD,
  MAX_CHAR_FILENAME_SM,
  MEDIUM_COMPONENT_SIZE,
  MIME_TYPES_MAP,
  SMALL_COMPONENT_SIZE,
} from '../../utils/Constants';
import P from '../Texts/P/P';

const DragAndDrop = ({
  disabled,
  fileTypes,
  handleBlur,
  maxFileSize,
  message,
  name,
  setValue,
  size,
  value,
}) => {
  const [
    dragOverHandler,
    dropHandler,
    errorMessage,
    fileData,
    handleDeleteClick,
    handleInputFileClick,
    inputFileRef,
    showErrorMessage,
    showFileNameArea,
  ] = useDraggableInputFile(
    setValue,
    fileTypes,
    maxFileSize,
    size,
    value,
    handleBlur
  );
  const spanVariantByStatus = {
    normal: 'neutral',
    error: 'danger',
  };
  const fileTypesToString = () => {
    if (fileTypes?.length > 1) {
      const fileTypesCopy = [...fileTypes];
      const lastFileType = fileTypesCopy.pop();
      return fileTypesCopy
        .map((type) => type.toUpperCase())
        .join(', ')
        .concat(` o ${lastFileType.toUpperCase()}`);
    }
    return fileTypes[0].toUpperCase();
  };

  const acceptFileTypes = () => {
    if (fileTypes?.length > 1) {
      const fileTypesCopy = [...fileTypes];
      return fileTypesCopy.map((type) => MIME_TYPES_MAP[type]).join(', ');
    }
    return MIME_TYPES_MAP[fileTypes[0]];
  };

  const textBySize = {
    [MEDIUM_COMPONENT_SIZE]:
      'Arrastrá y soltá un archivo, o hacé click para subirlo.',
    [SMALL_COMPONENT_SIZE]: (
      <>
        Arrastrá y soltá un archivo,
        <br />o hacé click para subirlo.
      </>
    ),
  };

  const iconDimensionBySize = {
    [MEDIUM_COMPONENT_SIZE]: 20,
    [SMALL_COMPONENT_SIZE]: 32,
  };

  const getStatus = () => {
    return message || showErrorMessage ? 'error' : 'normal';
  };

  const getMessage = () => {
    if (message !== undefined && Object.keys(fileData).length === 0) {
      return message;
    } else if (errorMessage !== null && Object.keys(fileData).length === 0) {
      return errorMessage;
    }
  };

  const nameFormat = (fileName) => {
    if (fileName) {
      const max =
        size === SMALL_COMPONENT_SIZE
          ? MAX_CHAR_FILENAME_SM
          : MAX_CHAR_FILENAME_MD;
      const parenthesis = '(...)';
      const symbolIndex = fileName.indexOf('.');
      const nameIntermediate = fileName.slice(0, symbolIndex);
      let fileNameResult = '';
      if (nameIntermediate.length > max) {
        const extension = fileName.slice(symbolIndex, fileName.length);
        fileNameResult =
          nameIntermediate.slice(0, max - 1) + parenthesis + extension;
      } else {
        fileNameResult = fileName;
      }
      return fileNameResult;
    }
  };

  const renderEmptyInput = () => (
    <>
      <S.DragAndDrop
        disabled={disabled}
        name={name}
        onDragOver={!disabled ? dragOverHandler : null}
        onDrop={!disabled ? dropHandler : null}
        size={size}
        status={getStatus()}
        visible={!showFileNameArea}
      >
        <S.DropBaseIcon size={size}>
          <Icon
            height={32}
            modifier='weak'
            name='upload_cloud_outline'
            variant='neutral'
            width={32}
          />
        </S.DropBaseIcon>
        <S.MainTextWrapper size={size}>
          <P
            align={size === SMALL_COMPONENT_SIZE ? 'center' : 'left'}
            modifier={disabled ? 'regular' : 'strong'}
            type='4'
            variant='neutral'
            weight='bold'
          >
            {textBySize[size]}
          </P>
          <Span type='4' variant='neutral'>
            {`${fileTypesToString()}. Peso máximo: ${maxFileSize}MB.`}
          </Span>
        </S.MainTextWrapper>
        <S.ActionWrapper size={size}>
          <S.FileInputLabel disabled={disabled} htmlFor={name}>
            Seleccionar archivo
          </S.FileInputLabel>
          <S.FileInput
            disabled={disabled}
            id={name}
            name={name}
            onChange={handleInputFileClick}
            ref={inputFileRef}
            type='file'
            accept={acceptFileTypes()}
          />
        </S.ActionWrapper>
      </S.DragAndDrop>
      {getStatus() === 'error' && (
        <S.HelpText status={getStatus()} visible={!showFileNameArea}>
          <Icon
            height={16}
            name='error_circle_outline'
            variant='danger'
            width={16}
          />
          {getMessage() && (
            <Span type='4' variant={spanVariantByStatus[getStatus()]}>
              {getMessage()}
            </Span>
          )}
        </S.HelpText>
      )}
    </>
  );

  const renderFullfilledInput = () => (
    <S.DragAndDrop
      name={name}
      onDrop={dropHandler}
      size={size}
      visible={showFileNameArea}
    >
      <S.SuccessIcon size={size}>
        <Icon
          name='check_circle_outline'
          variant='success'
          height={iconDimensionBySize[size]}
          width={iconDimensionBySize[size]}
        />
      </S.SuccessIcon>
      <S.MainTextWrapper size={size}>
        <Span type='4' variant='secondary-1' modifier='strong'>
          {nameFormat(fileData.name)}
        </Span>
      </S.MainTextWrapper>
      <S.ActionWrapper size={size} deleteAction>
        <S.FileInputLabel onClick={handleDeleteClick}>
          Eliminar
        </S.FileInputLabel>
      </S.ActionWrapper>
    </S.DragAndDrop>
  );

  return (
    <S.DragAndDropWrapper>
      {renderEmptyInput()}
      {renderFullfilledInput()}
    </S.DragAndDropWrapper>
  );
};

DragAndDrop.defaultProps = {
  disabled: false,
  size: 'md',
};

DragAndDrop.propTypes = {
  disabled: PropTypes.bool,
  fileTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(MIME_TYPES_MAP)))
    .isRequired,
  maxFileSize: PropTypes.number.isRequired,
  message: PropTypes.string,
  name: PropTypes.string.isRequired,
  handlerBlur: PropTypes.func,
  setValue: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  value: PropTypes.instanceOf(File),
};

export default DragAndDrop;
